import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { SidebarProvider, useSidebar } from "../../components/ui/sidebar";
import { useCanvas } from "../../contexts/CanvasContext";
import { AppSidebar } from "../../features/sidebar/components/app-sidebar";
import { cn } from "../../lib/utils";
import CanvasInterface from "./CanvasInterface";
import { NavigationHeader } from "../Chat/header/NavigationHeader";

const CanvasLayout = ({ children, className }) => {
  const { canvasState } = useCanvas();

  // Read the current sidebar state from cookie to maintain state when opening canvas
  const getSidebarStateFromCookie = () => {
    const cookies = document.cookie.split(";");
    const sidebarCookie = cookies.find((cookie) =>
      cookie.trim().startsWith("sidebar_state=")
    );
    if (sidebarCookie) {
      const value = sidebarCookie.split("=")[1];
      return value === "true";
    }
    return true; // Default to open if no cookie
  };

  // Always render children - when canvas is closed, just show children
  // When canvas is open, show the overlay
  return (
    <>
      {/* Always render the base children */}
      <div className={cn("h-full w-full", className)}>{children}</div>

      {/* Canvas overlay - only show when open */}
      <AnimatePresence>
        {canvasState.isOpen && (
          <motion.div
            key="canvas-layout"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed inset-0 w-screen h-screen dark:bg-[#2F2F2F] bg-[#FAFAFA] z-[99999]"
            style={{ top: 0, left: 0, right: 0, bottom: 0 }}
          >
            {/* Wrap canvas content with its own SidebarProvider, preserving current state */}
            <SidebarProvider
              defaultOpen={getSidebarStateFromCookie()}
              className="h-full w-full"
            >
              <CanvasLayoutContent className={className}>
                {children}
              </CanvasLayoutContent>
            </SidebarProvider>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Separate component that uses useSidebar hook
const CanvasLayoutContent = ({ children }) => {
  useSidebar(); // Hook must be called, but we don't need the values

  return (
    <div className="flex h-full w-full">
      {/* Sidebar - always show, collapses to icons */}
      <AppSidebar />

      {/* Main Canvas Content */}
      <div className="flex-1 h-full flex">
        <PanelGroup direction="horizontal" className="h-full w-full">
          {/* Chat Panel */}
          <Panel
            defaultSize={35}
            minSize={25}
            maxSize={50}
            className="min-w-0 relative"
          >
            <motion.div
              initial={{ x: -100 }}
              animate={{ x: 0 }}
              exit={{ x: -100 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="h-full border-r border-gray-600 dark:border-gray-600 border-gray-200 bg-background flex flex-col"
            >
              {/* Canvas Chat Topbar */}
              <NavigationHeader isCanvas={true} />

              {/* Chat Content */}
              <div className="flex-1 overflow-hidden">{children}</div>
            </motion.div>
          </Panel>

          {/* Resize Handle */}
          <PanelResizeHandle className="bg-transparent hover:bg-gray-500/10 dark:hover:bg-gray-500/10 hover:bg-gray-300/20 transition-colors duration-200" />

          {/* Canvas Panel */}
          <Panel defaultSize={65} minSize={50} maxSize={75} className="min-w-0">
            <motion.div
              initial={{ x: 100 }}
              animate={{ x: 0 }}
              exit={{ x: 100 }}
              transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
              className="h-full shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)] dark:shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)] shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.05)]"
              style={{ backgroundColor: "#2F2F2F" }}
            >
              <CanvasInterface className="h-full" />
            </motion.div>
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
};

CanvasLayout.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

CanvasLayoutContent.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CanvasLayout;
